import Axios from 'axios'

import { API_PUBLIC_URL } from '@/config/env'
import { useAppStore } from '@/stores/appStore'
import { PublicDto } from '@/types'

/**
 * Refresh access token using refresh token (pure API call)
 * This is a pure function that can be called from anywhere (not just React components)
 * @param refreshToken The refresh token to use
 * @returns Promise with new tokens
 */
export const refreshTokenAsync = async (refreshToken: string): Promise<PublicDto['UserTokenDto']> => {
  // Use Axios directly (not our configured instance) to avoid circular dependency
  const response = await Axios.post<PublicDto['UserTokenDto']>(`${API_PUBLIC_URL}/auth/refresh`, {
    refreshToken,
  })

  return response.data
}

let inFlightRefresh: Promise<PublicDto['UserTokenDto']> | null = null

const performRefresh = async (refreshToken: string): Promise<PublicDto['UserTokenDto']> => {
  const accessTokenBefore = useAppStore.getState().accessToken

  try {
    const tokens = await refreshTokenAsync(refreshToken)

    // Automatically update store with new tokens
    useAppStore.getState().actions.setTokens({
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresIn: tokens.expiresIn ?? 3600, // Default to 1h if not provided
    })

    return tokens
  } catch (error) {
    const { idToken, accessToken, refreshToken: currentRefreshToken, tokenExpiresAt } = useAppStore.getState()

    // Another flow (or another tab) already rotated the tokens while this call was in flight:
    // the backend rejected an already consumed refresh token, which is not an authentication failure.
    if (accessToken && idToken && currentRefreshToken && accessToken !== accessTokenBefore) {
      return {
        idToken,
        accessToken,
        refreshToken: currentRefreshToken,
        expiresIn: tokenExpiresAt ? Math.max(0, Math.round((tokenExpiresAt - Date.now()) / 1000)) : undefined,
      }
    }

    throw error
  }
}

/**
 * Refresh access token and automatically update the app store.
 * Concurrent callers share a single in-flight request so a rotated refresh token is never replayed.
 * @param refreshToken The refresh token to use
 * @returns Promise with new tokens
 */
export const refreshTokenAndUpdateStore = (refreshToken: string): Promise<PublicDto['UserTokenDto']> => {
  inFlightRefresh ??= performRefresh(refreshToken).finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}
